import React from 'react';
import { cn } from '@/lib/utils';

/**
 * First-time or empty archive surface: fixed in the layout (not hover, not floating).
 * Parchment block with optional icon — reuse for documents, stories, exports, etc.
 */
export function ArchiveEmptyState({
  icon: Icon,
  title,
  description,
  children,
  actions,
  className,
  /** 'start' keeps copy aligned with tab content; 'center' for narrow marketing-style blocks */
  align = 'start',
  ...rest
}) {
  const isStart = align === 'start';

  return (
    <div
      className={cn(
        'rounded-[1.5rem] border border-[hsl(38_14%_86%)]/95 bg-gradient-to-b from-[hsl(42_28%_99%)] via-[hsl(40_24%_98%)] to-[hsl(38_22%_96%)] px-6 py-10 shadow-[inset_0_1px_0_hsl(42_40%_100%_/_0.55),0_12px_40px_hsl(28_12%_18%/_0.05)] sm:px-10 sm:py-12',
        isStart ? 'text-left' : 'text-center',
        className
      )}
      {...rest}
    >
      <div
        className={cn(
          'flex flex-col gap-5 sm:gap-6',
          isStart ? 'sm:flex-row sm:items-start' : 'items-center'
        )}
      >
        {Icon ? (
          <div
            className={cn(
              'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[hsl(38_16%_88%)] bg-[hsl(42_26%_99%)] text-[#8b7355] ring-1 ring-[#c9a961]/12',
              !isStart && 'mx-auto sm:mx-0'
            )}
            aria-hidden
          >
            <Icon className="h-7 w-7" />
          </div>
        ) : null}
        <div className="min-w-0 flex-1 space-y-3">
          {title ? (
            <h3 className="font-serif text-lg font-medium tracking-tight text-[hsl(24_12%_18%)]">{title}</h3>
          ) : null}
          {description ? (
            <p className="max-w-2xl text-[0.9375rem] leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
          {children}
          {actions ? (
            <div
              className={cn(
                'flex flex-col gap-2.5 pt-2 sm:flex-row sm:flex-wrap sm:items-center',
                !isStart && 'items-center sm:justify-center'
              )}
            >
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
